import { Component, inject, DestroyRef, signal, computed } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Commands } from '../../services/commands/commands';
import { Events } from '../../services/events/events';
import { RoomSetup } from '../../../../components/room-setup/room-setup';
import { Store } from '../../services/store/store';

@Component({
  selector: 'app-waiting-room',
  imports: [RoomSetup, RouterLink],
  templateUrl: './waiting-room.html',
  styleUrl: './waiting-room.css'
})
export class WaitingRoom {

  // PROPERTIES

  private destroyRef = inject(DestroyRef);

  // SIGNALS

  protected room_id = signal<string>('');
  
  protected is_waiting_for_game_end = signal<boolean>(false);
  protected player_names = signal<string[]>([]);

  protected is_name_submitted = signal<boolean>(false);
  protected is_game_start_requested = signal<boolean>(false);
  protected name_input = signal<string>('');

  // COMPUTED SIGNALS

  protected local_player_name = computed(() => {
    return this.name_input().trim();
  });

  protected is_name_valid = computed(() => {

    return (
      (this.local_player_name().length > 0) &&
      (!this.player_names().includes(this.local_player_name()))
    );

  });

  // ROOM ID LISTENER

  public setRoomId(room_id: string): void {

    this.room_id.set(room_id);

  }

  // EVENT LISTENERS

  private playerJoinedListener(): void {

    this.events.onPlayerJoined()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {

          if (data.is_game_started)
            this.is_waiting_for_game_end.set(true);
          
          else {
            this.is_waiting_for_game_end.set(false);
            this.player_names.set(data.player_names);
          }

        }
      });

  }

  private playerChoosenListener(): void {

    this.events.onPlayersUpdate()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {

          if (data.is_added)
            this.player_names.update(names => [...names, data.name]);

          else
            this.player_names.update(names => names.filter(name => name !== data.name));

        }
      });

  }

  private gameStartedListener(): void {

    this.events.onGameStarted()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {

          if (this.is_name_submitted()){

            // set things in the store
            this.store.setPlayerNames(this.player_names(), this.local_player_name());
            this.store.setStartName(data.start_name);
            this.store.setWord(data.word);

            // go to board page
            this.router.navigate([`/impostor/board/${this.room_id()}`])

          }

          else
            this.is_waiting_for_game_end.set(true);

        }
      });

  }

  private gameEndedListener(): void {

    this.events.onGameEnded()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {

          this.is_waiting_for_game_end.set(false);
          this.player_names.set([]);

        }
      });

  }

  public initializeListeners(): void {

    this.playerJoinedListener();
    this.playerChoosenListener();
    this.gameStartedListener();
    this.gameEndedListener();

  }

  // COMMANDS

  public updateNameInput(event: Event): void {

    const target = event.target as HTMLInputElement;
    this.name_input.set(target.value);

  }

  public submitName(): void {

    this.commands.chooseName(this.local_player_name(), () => {
      this.is_name_submitted.set(true);
    });

  }

  public startGame(): void {

    this.is_game_start_requested.set(true);
    this.commands.startGame();

  }

  // LIFECYCLE

  constructor(
    private router: Router,
    private commands: Commands,
    private events: Events,
    private store: Store
  ) {}

}
